import React, { useState, useEffect, useRef, useCallback } from 'react';
import BallCanvas from './components/BallCanvas';
import Drawer from './components/Drawer';
import Popout from './components/Popout';
import additivesFullData from './additives_full.json';
import Matter from 'matter-js';
import './App.css';
import categoryColors from './colors';
import { Typography, Card, CardContent } from '@mui/material';

const DRAWER_WIDTH = 280;

const App = () => {
  const [categories, setCategories] = useState({});
  const [balls, setBalls] = useState([]);
  const [boundaries, setBoundaries] = useState([]);
  const [activeAdditives, setActiveAdditives] = useState([]);
  const [activeCategories, setActiveCategories] = useState([]);
  const [selectedAdditive, setSelectedAdditive] = useState(null);
  const engineRef = useRef(Matter.Engine.create());
  const runnerRef = useRef(Matter.Runner.create());
  const boundariesRef = useRef([]);
  const canvasSizeRef = useRef({ w: 0, h: 0 });

  useEffect(() => {
    const loadedCategories = {};
    additivesFullData.additives.forEach(category => {
      loadedCategories[category.category] = category.additives;
    });
    setCategories(loadedCategories);
  }, []);

  const rebuildBoundaries = useCallback(() => {
    const engine = engineRef.current;
    const { w, h } = canvasSizeRef.current;
    if (w === 0 || h === 0) return;

    const prev = boundariesRef.current;
    if (prev.length) {
      prev.forEach(b => Matter.World.remove(engine.world, b));
    }

    const next = createBoundaries(w, h);
    boundariesRef.current = next;
    setBoundaries(next);
    next.forEach(b => Matter.World.add(engine.world, b));
  }, []);

  const handleCanvasSize = useCallback((w, h) => {
    const prev = canvasSizeRef.current;
    canvasSizeRef.current = { w, h };
    rebuildBoundaries();

    if (prev.w > 0 && prev.h > 0) {
      const scaleX = w / prev.w;
      const scaleY = h / prev.h;
      const engine = engineRef.current;
      Matter.Composite.allBodies(engine.world).forEach(body => {
        if (!body.isStatic) {
          Matter.Body.scale(body, scaleX, scaleX);
          Matter.Body.setPosition(body, {
            x: body.position.x * scaleX,
            y: body.position.y * scaleY
          });
        }
      });
    }
  }, [rebuildBoundaries]);

  useEffect(() => {
    const engine = engineRef.current;
    const runner = runnerRef.current;
    Matter.Runner.run(runner, engine);

    return () => {
      Matter.Runner.stop(runner);
      Matter.World.clear(engine.world);
      Matter.Engine.clear(engine);
    };
  }, []);

  const handleCategoryChange = (category) => {
    if (activeCategories.includes(category)) {
      const ballsToRemove = balls.filter(ball => ball.category === category);
      removeBallsFromWorld(ballsToRemove);
      setBalls(prevBalls => prevBalls.filter(ball => ball.category !== category));
      setActiveAdditives(prevAdditives => prevAdditives.filter(additive => additive.category !== category));
      setActiveCategories(prevCategories => prevCategories.filter(cat => cat !== category));
    } else {
      const newBalls = generateStackedBalls(category);
      setActiveAdditives(prevAdditives => [
        ...prevAdditives,
        ...categories[category].map(additive => ({ ...additive, category }))
      ]);
      setActiveCategories(prevCategories => [...prevCategories, category]);
      addBallsOneByOne(newBalls);
    }
  };

  const addBallsOneByOne = (balls, index = 0) => {
    if (index < balls.length) {
      setBalls(prevBalls => [...prevBalls, balls[index]]);
      setTimeout(() => addBallsOneByOne(balls, index + 1), 50);
    }
  };

  const createBall = (x, y, radius, category) => {
    return Matter.Bodies.polygon(x, y, 6, radius, {
      restitution: 0,
      friction: 0.1,
      frictionAir: 0.1,
      frictionStatic: 0.5,
      density: 0.001,
      isStatic: false,
      render: {
        fillStyle: getColorForCategory(category) || '#FF5733'
      }
    });
  };

  const generateStackedBalls = (category) => {
    const canvasWidth = canvasSizeRef.current.w || (window.innerWidth - DRAWER_WIDTH);
    const BASE_WIDTH = 1000;
    const radius = Math.max(8, 20 * (canvasWidth / BASE_WIDTH));
    const additives = categories[category] || [];
    const balls = [];
    const cols = 5;
    const centerX = canvasWidth / 2;
    const topY = 100;

    additives.forEach((additive, index) => {
      let x = centerX + (index % cols - Math.floor(cols / 2)) * (radius * 2) + 10;
      const y = topY + Math.floor(index / cols) * (radius * 2);
      x = Math.max(radius, Math.min(canvasWidth - radius, x));
      const ball = createBall(x, y, radius, category);
      ball.additive = additive;
      ball.category = category;
      balls.push(ball);
    });

    return balls;
  };

  const removeBallsFromWorld = (ballsToRemove) => {
    const engine = engineRef.current;
    ballsToRemove.forEach(ball => {
      Matter.World.remove(engine.world, ball);
    });
  };

  const getColorForCategory = (category) => {
    return categoryColors[category] || "#333";
  };

  return (
    <div style={{ display: 'flex', backgroundColor: '#ffffff', height: '100%', overflow: 'hidden' }}>
      <Drawer
        additives={activeAdditives}
        categories={categories}
        activeCategories={activeCategories}
        onCategoryChange={handleCategoryChange}
      />
      <div style={{ display: 'flex', flexDirection: 'column', flexGrow: 1, overflow: 'hidden', marginLeft: `${DRAWER_WIDTH}px`, height: '100%' }}>
        <div style={{ position: 'relative', flex: 1, overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: 0, left: 0, right: '16px', padding: 'clamp(4px, 0.6vw, 8px)', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(clamp(60px, 8vw, 120px), 1fr))', gap: 'clamp(2px, 0.4vw, 5px)', zIndex: 1000 }}>
            {activeAdditives.map((additive, index) => (
              <AdditiveCard key={index} additive={additive} onClick={() => setSelectedAdditive(additive)} />
            ))}
          </div>
          <BallCanvas balls={balls} engine={engineRef.current} drawerWidth={DRAWER_WIDTH} boundaries={boundaries} onSize={handleCanvasSize} />
        </div>
        <Popout additive={selectedAdditive} onClose={() => setSelectedAdditive(null)} />
      </div>
    </div>
  );
};

const createBoundaries = (canvasWidth, canvasHeight) => {
  const t = 50;
  const glassColor = 'rgba(123, 31, 162, 0.08)';

  // Invisible safety walls and ceiling
  const leftWall = Matter.Bodies.rectangle(
    -t / 2, canvasHeight / 2, t, canvasHeight * 2,
    { isStatic: true, render: { visible: false } }
  );
  const rightWall = Matter.Bodies.rectangle(
    canvasWidth + t / 2, canvasHeight / 2, t, canvasHeight * 2,
    { isStatic: true, render: { visible: false } }
  );
  const ceiling = Matter.Bodies.rectangle(
    canvasWidth / 2, -t / 2, canvasWidth + t * 2, t,
    { isStatic: true, render: { visible: false } }
  );

  // Wineglass bowl — parabolic curve
  const bowlBodies = [];
  const segments = 14;
  const centerX = canvasWidth / 2;
  const bowlBottom = canvasHeight * 0.92;
  const bowlWidth = canvasWidth + t;
  const curveDepth = canvasHeight * 0.18;

  for (let i = 0; i < segments; i++) {
    const t1 = i / segments;
    const t2 = (i + 1) / segments;

    const x1 = centerX - bowlWidth / 2 + t1 * bowlWidth;
    const x2 = centerX - bowlWidth / 2 + t2 * bowlWidth;
    // Parabola: rim high at edges, deepest at center
    const y1 = bowlBottom - curveDepth * Math.pow(2 * t1 - 1, 2);
    const y2 = bowlBottom - curveDepth * Math.pow(2 * t2 - 1, 2);

    const cx = (x1 + x2) / 2;
    const cy = (y1 + y2) / 2;
    const len = Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2);
    const angle = Math.atan2(y2 - y1, x2 - x1);

    bowlBodies.push(Matter.Bodies.rectangle(cx, cy, len + 2, 8, {
      isStatic: true,
      angle: angle,
      render: { fillStyle: glassColor },
      chamfer: { radius: 4 }
    }));
  }

  return [leftWall, rightWall, ceiling, ...bowlBodies];
};

const AdditiveCard = ({ additive, onClick }) => {
  return (
    <Card
      onClick={onClick}
      style={{
        width: '100%',
        backgroundColor: 'rgba(250, 249, 246, 0.8)',
        cursor: 'pointer',
        borderRadius: 'clamp(8px, 1.2vw, 16px)',
        boxSizing: 'border-box',
        transition: 'background-color 0.3s ease',
        height: 'clamp(20px, 3.5vw, 37px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden'
      }}
    >
      <CardContent style={{ textAlign: 'center', padding: 'clamp(2px, 0.4vw, 4px)' }}>
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'Roboto, sans-serif !important',
            fontSize: 'clamp(0.45rem, 1vw, 0.875rem)',
            lineHeight: '1.0',
            display: '-webkit-box',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 2,
          }}
        >
          {additive.name}
        </Typography>
      </CardContent>
    </Card>
  );
};

export default App;
